/**
 * Shared React cell-renderer components for table and kanban views.
 *
 * Imports all colour/utility helpers from chartUtils so these components
 * can be reused across TableView, KanbanBoard, and any future chart types
 * that render person, status, or progress data.
 */

import {
	avatarColour,
	initials,
	STATUS_COLOURS,
} from "./chartUtils.js";

// ---------------------------------------------------------------------------
// PersonCell
// ---------------------------------------------------------------------------

/**
 * Renders a coloured avatar circle containing the person's initials,
 * followed by their full name.
 */
export function PersonCell({ value }: { value: string }) {
	const colour = avatarColour(value);
	return (
		<span className="table-view-person">
			<span
				className="table-view-avatar"
				style={{ background: colour }}
				aria-hidden="true"
			>
				{initials(value)}
			</span>
			<span className="table-view-person-name">{value}</span>
		</span>
	);
}

// ---------------------------------------------------------------------------
// StatusBadge
// ---------------------------------------------------------------------------

/**
 * Renders a coloured pill badge with a leading dot.
 * `colourKey` must be one of the keys in STATUS_COLOURS (e.g. "green", "amber").
 * Falls back to "grey" if the key is unrecognised.
 */
export function StatusBadge({
	colourKey,
	label,
}: {
	colourKey: string;
	label: string;
}) {
	const colours = STATUS_COLOURS[colourKey] ?? STATUS_COLOURS["grey"]!;
	return (
		<span
			className="table-view-badge"
			style={{ background: colours.bg, color: colours.text }}
		>
			<span
				className="table-view-badge-dot"
				style={{ background: colours.dot }}
				aria-hidden="true"
			/>
			{label}
		</span>
	);
}

// ---------------------------------------------------------------------------
// ProgressCell
// ---------------------------------------------------------------------------

/**
 * Renders a horizontal progress bar track + fill alongside a percentage label.
 * Clamps `value` to [0, 100].
 */
export function ProgressCell({ value }: { value: number }) {
	const pct = Math.min(100, Math.max(0, value));
	return (
		<span className="table-view-progress">
			<span className="table-view-progress-bar-track">
				<span
					className="table-view-progress-bar-fill"
					style={{ width: `${pct}%` }}
					aria-hidden="true"
				/>
			</span>
			<span className="table-view-progress-label">{pct}%</span>
		</span>
	);
}
